import { ClaimDetailsContext } from "client/context/ClaimDetails";
import useAnswerClaimInfoRequest from "client/hooks/data/user/useAnswerClaimInfoRequest";
import { ClaimStatus } from "client/types";
import React, { useContext, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../../../ui-components/Button";
import Modal from "../../../../ui-components/Modal";
import {
  ButtonsWrapper,
  ContentContainer,
  FinalSubtitle,
  QuestionCard,
  QuestionLabel,
  QuestionsContainer,
  QuestionText,
  ResponseLabel,
  StyledTextarea,
  Subtitle,
  SuccessModalContainer,
  Title,
  TitleContainer,
} from "./styles";

const AnswerQuestionsModal = () => {
  const { claim, unasweredRequest, claimInfoRequests } = useContext(ClaimDetailsContext);
  const [response, setResponse] = useState("");
  const { mutate: answerClaimInfoRequest, isPending, isSuccess } = useAnswerClaimInfoRequest();
  const location = useLocation();
  const history = useHistory();

  const isModalOpen = location.search.includes("answerQuestions=true");
  const currentInfoRequest = claimInfoRequests?.results.find(request => !request.responded_at);
  const partnerName = claim?.lost_location?.partner?.name;
  const question = currentInfoRequest?.request_message;

  const onClose = () => {
    history.push(`${location.pathname}`);
  };

  const handleSubmit = async () => {
    try {
      await answerClaimInfoRequest({
        claimId: claim?.id ?? "",
        infoRequestId: unasweredRequest?.id ?? "",
        responseMessage: response,
      });
      setResponse("");
    } catch (error) {
      toast.error("Failed to answer the question. Please try again.");
      console.error(error);
    }
  };

  if (isSuccess) {
    return (
      <Modal
        isOpened={isModalOpen}
        setIsOpened={() => {
          history.push(`${location.pathname}`);
        }}
        heading={
          <SuccessModalContainer>
            <Title>Thanks for your response.</Title>
            <FinalSubtitle>
              We've received your response and passed it along to the team reviewing your claim. If
              there’s a match, you’ll be the first to know!
            </FinalSubtitle>
          </SuccessModalContainer>
        }
        description={
          <ContentContainer>
            <ButtonsWrapper style={{ justifyContent: "center" }}>
              <Button variant="primary" onClick={onClose} aria-label="Close modal">
                Close
              </Button>
            </ButtonsWrapper>
          </ContentContainer>
        }
      />
    );
  }

  if (!unasweredRequest) {
    return null;
  }

  return (
    <Modal
      isOpened={isModalOpen}
      setIsOpened={onClose}
      hasCloseButton
      heading={
        <TitleContainer>
          <Title>Answer the Question Below</Title>
          <Subtitle>
            Help the partner by giving more details about your lost item. Please be as descriptive
            as possible.
          </Subtitle>
        </TitleContainer>
      }
      description={
        <ContentContainer>
          <QuestionsContainer>
            <QuestionCard>
              <QuestionLabel>{partnerName} staff asked the following:</QuestionLabel>
              <QuestionText>{question}</QuestionText>
            </QuestionCard>

            <div className="d-flex flex-column gap-1">
              <ResponseLabel htmlFor="response">Add your response</ResponseLabel>
              <StyledTextarea
                id="response"
                value={response}
                onChange={e => setResponse(e.target.value)}
                placeholder="Be as descriptive as possible. This will help us identify your item faster."
              />
            </div>
          </QuestionsContainer>
          <ButtonsWrapper>
            <Button variant="outline" onClick={onClose} aria-label="Cancel and close modal">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!response.trim() || isPending}
              aria-label="Submit Response"
            >
              Submit Response
            </Button>
          </ButtonsWrapper>
        </ContentContainer>
      }
    />
  );
};

export function hasNeedMoreInfoStatus({
  hasUnansweredInfoRequests,
  status,
}: {
  hasUnansweredInfoRequests: boolean;
  status: string | undefined;
}) {
  return (
    hasUnansweredInfoRequests &&
    [ClaimStatus.HAS_MATCHES, ClaimStatus.NO_MATCHES, ClaimStatus.NEW].includes(
      status as ClaimStatus,
    )
  );
}

export default AnswerQuestionsModal;
